<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  task: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['save', 'close']);

const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');

const showModal = ref(true);

const formData = ref({
  title: '',
  description: '',
  dueDate: '',
  assignedTo: '',
  status: 'Pendiente',
});

const isEditing = computed(() => !!props.task);

const modalTitle = computed(() =>
  isEditing.value ? 'TASKS.EDIT_TASK' : 'TASKS.CREATE_TASK'
);

const isValid = computed(() => {
  return formData.value.title.trim() && formData.value.dueDate;
});

const handleSave = () => {
  if (!isValid.value) return;
  emit('save', formData.value);
};

const handleClose = () => {
  showModal.value = false;
  emit('close');
};

onMounted(async () => {
  // Cargar agentes si no están cargados
  if (!agents.value || agents.value.length === 0) {
    await store.dispatch('agents/get');
  }

  if (props.task) {
    formData.value = {
      title: props.task.title,
      description: props.task.description,
      dueDate: props.task.dueDate,
      assignedTo: props.task.assignedTo,
      status: props.task.status,
    };
  } else {
    // Set default due date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    formData.value.dueDate = tomorrow.toISOString().split('T')[0];
    formData.value.assignedTo = currentUser.value?.name || '';
  }
});
</script>

<template>
  <woot-modal v-model:show="showModal" :on-close="handleClose">
    <woot-modal-header
      :header-title="$t(modalTitle)"
      :header-content="
        isEditing
          ? $t('TASKS.EDIT_DESCRIPTION')
          : $t('TASKS.CREATE_DESCRIPTION')
      "
    />
    <form
      class="modal-content w-full pt-2 px-5 pb-6"
      @submit.prevent="handleSave"
    >
      <!-- Title -->
      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('TASKS.FORM.TITLE') }} *
        </label>
        <input
          v-model="formData.title"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
          :placeholder="$t('TASKS.FORM.TITLE_PLACEHOLDER')"
          required
        />
      </div>

      <!-- Description -->
      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('TASKS.FORM.DESCRIPTION') }}
        </label>
        <textarea
          v-model="formData.description"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
          rows="3"
          :placeholder="$t('TASKS.FORM.DESCRIPTION_PLACEHOLDER')"
        />
      </div>

      <!-- Due Date -->
      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('TASKS.FORM.DUE_DATE') }} *
        </label>
        <input
          v-model="formData.dueDate"
          type="date"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
          required
        />
      </div>

      <!-- Assigned To -->
      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('TASKS.FORM.ASSIGNED_TO') }}
        </label>
        <select
          v-model="formData.assignedTo"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="">{{ $t('TASKS.SELECT_AGENT') }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.name">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <!-- Status -->
      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('TASKS.FORM.STATUS') }}
        </label>
        <select
          v-model="formData.status"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="Pendiente">{{ $t('TASKS.STATUS.PENDING') }}</option>
          <option value="En Proceso">
            {{ $t('TASKS.STATUS.IN_PROGRESS') }}
          </option>
          <option value="Completada">{{ $t('TASKS.STATUS.COMPLETED') }}</option>
        </select>
      </div>

      <!-- Footer buttons -->
      <div class="flex justify-end gap-2 mt-6">
        <NextButton
          variant="smooth"
          color="slate"
          :label="$t('TASKS.FORM.CANCEL')"
          @click="handleClose"
        />
        <NextButton
          type="submit"
          :label="$t('TASKS.FORM.SAVE')"
          :disabled="!isValid"
        />
      </div>
    </form>
  </woot-modal>
</template>
